import { useEffect, useState } from 'react'
import { Input } from '@/components/ui/Input'
import { SourceBadgeChip } from '@/components/ui/SourceBadge'
import { filledFill, focusRing, quietFill } from '@/components/ui/buttonStyles'
import {
  fetchAdminSourceBadges,
  createSourceBadge,
  updateSourceBadge,
  deleteSourceBadge,
} from '@/api/admin'
import type { SourceBadgeConfig } from '@/types/sourceBadge'

/** Honey, the same hue the AI marker carried before badges were configurable. */
const DEFAULT_COLOR = '#8a6516'

const button = 'rounded-md px-3 py-2 text-xs font-normal transition-colors duration-150'

export function SourceBadgesTab({ active }: { active: boolean }) {
  const [badges, setBadges] = useState<SourceBadgeConfig[] | null>(null)
  const [knownSources, setKnownSources] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!active || badges) return
    fetchAdminSourceBadges()
      .then((res) => {
        setBadges(res.data.data)
        setKnownSources(res.data.known_sources)
      })
      .catch(() => setError('Could not load the badges. Reload to try again.'))
  }, [active, badges])

  if (!active) return null
  if (error) return <p className="font-serif text-sm text-body">{error}</p>

  return (
    <div className="max-w-2xl">
      <div className="space-y-3">
        {badges?.length === 0 && (
          <p className="font-serif text-sm text-muted">
            No badges yet. Add one below and every sense from that source starts carrying it.
          </p>
        )}
        {badges?.map((badge) => (
          <BadgeRow
            key={badge.id}
            badge={badge}
            onRemoved={() => setBadges((rows) => rows?.filter((r) => r.id !== badge.id) ?? null)}
          />
        ))}
      </div>

      <AddBadgeForm
        knownSources={knownSources}
        onAdded={(badge) => setBadges((rows) => [...(rows ?? []), badge])}
      />
    </div>
  )
}

/* ─── One configured badge ──────────────────────────────── */

function BadgeRow({ badge, onRemoved }: { badge: SourceBadgeConfig; onRemoved: () => void }) {
  const [draft, setDraft] = useState(badge)
  const [status, setStatus] = useState<string | null>(null)

  function save() {
    setStatus(null)
    updateSourceBadge(badge.id, { label: draft.label, color: draft.color })
      .then(() => setStatus('Saved.'))
      .catch(() => setStatus('Could not save that badge.'))
  }

  return (
    <div className="rounded-lg border border-border-divider bg-surface-bg/40 px-4 py-3.5">
      {/* Identity on top, editors below: which source this is, and what it will
          look like once saved. */}
      <div className="flex items-center justify-between gap-3">
        <p className="font-mono text-sm text-body">{badge.source}</p>
        <SourceBadgeChip label={draft.label || badge.source} color={draft.color} />
      </div>

      <div className="mt-3 flex flex-wrap items-start gap-4">
        <ColorField
          id={`badge-color-${badge.id}`}
          value={draft.color}
          onChange={(color) => setDraft({ ...draft, color })}
          name={`Colour for ${badge.source}`}
        />
        <Input
          id={`badge-label-${badge.id}`}
          label="Label"
          value={draft.label}
          maxLength={24}
          onChange={(e) => setDraft({ ...draft, label: e.target.value })}
          className="w-36"
        />
      </div>

      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={save}
          aria-label={`Save badge for ${badge.source}`}
          className={`${button} ${filledFill} ${focusRing}`}
        >
          Save
        </button>
        <button
          type="button"
          onClick={() => deleteSourceBadge(badge.id).then(onRemoved).catch(() => setStatus('Could not remove that badge.'))}
          aria-label={`Remove badge for ${badge.source}`}
          className={`${button} ${quietFill} ${focusRing}`}
        >
          Remove
        </button>
        <p aria-live="polite" className="font-serif text-sm text-muted">
          {status}
        </p>
      </div>
    </div>
  )
}

/* ─── New badge ─────────────────────────────────────────── */

function AddBadgeForm({
  knownSources,
  onAdded,
}: {
  knownSources: string[]
  onAdded: (badge: SourceBadgeConfig) => void
}) {
  const [source, setSource] = useState('')
  const [label, setLabel] = useState('')
  const [color, setColor] = useState(DEFAULT_COLOR)
  const [error, setError] = useState<string | null>(null)

  function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    createSourceBadge({ source, label, color })
      .then((res) => {
        onAdded(res.data.data)
        setSource('')
        setLabel('')
      })
      .catch(() => setError('Could not add that badge. Each source can only have one.'))
  }

  return (
    <form onSubmit={submit} aria-labelledby="add-badge" className="mt-10">
      <h3 id="add-badge" className="font-serif text-lg text-heading">
        Add a badge
      </h3>

      <div className="mt-4 flex flex-wrap items-start gap-4">
        <ColorField id="new-badge-color" value={color} onChange={setColor} />
        <div className="w-40">
          <Input
            id="new-badge-source"
            label="Source"
            list="known-sources"
            value={source}
            placeholder="e.g. ai"
            onChange={(e) => setSource(e.target.value)}
            required
          />
          <datalist id="known-sources">
            {knownSources.map((known) => (
              <option key={known} value={known} />
            ))}
          </datalist>
        </div>
        <Input
          id="new-badge-label"
          label="Label"
          value={label}
          maxLength={24}
          placeholder="e.g. AI"
          onChange={(e) => setLabel(e.target.value)}
          required
          className="w-36"
        />
        <button
          type="submit"
          disabled={!source || !label}
          className={`${button} min-h-11 self-end disabled:opacity-50 ${filledFill} ${focusRing}`}
        >
          Add badge
        </button>
      </div>

      {label && (
        <p className="mt-4 flex items-center gap-2 font-serif text-sm text-muted">
          Preview
          <SourceBadgeChip label={label} color={color} />
        </p>
      )}
      {error && (
        <p role="alert" className="mt-3 text-sm font-light text-error">
          {error}
        </p>
      )}
    </form>
  )
}

/* ─── Colour wheel ──────────────────────────────────────── */

/**
 * The native colour input: it opens the platform's own wheel, which beats any
 * picker we could build and is keyboard-reachable for free. The hex sits beside
 * it because a swatch alone tells you nothing you can write down or compare.
 */
function ColorField({
  id,
  value,
  onChange,
  name,
}: {
  id: string
  value: string
  onChange: (value: string) => void
  /** Spoken name, when several rows each need their own "Colour" on one screen. */
  name?: string
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-xs uppercase tracking-widest text-label font-light">
        Colour
      </label>
      <span className="flex items-center gap-2">
        <input
          id={id}
          type="color"
          aria-label={name}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`h-11 w-11 cursor-pointer rounded-md border border-border-divider bg-transparent p-1 ${focusRing}`}
        />
        <span className="font-mono text-xs text-muted">{value}</span>
      </span>
    </div>
  )
}
